import {inject, Injectable} from '@angular/core';
import {ActivitiesConfigurationService} from '@modules/activities/core/services/activities-configuration.service';
import {TypologyLabel} from '@modules/activities/core/typologies/typology.label';
import {CommunicationCenterService} from '@modules/communication-center';
import {OctopusConnectService} from 'octopus-connect';
import {combineLatest, Observable, shareReplay} from 'rxjs';
import {catchError, map, take, tap} from 'rxjs/operators';

export type Typology = { label: TypologyLabel, id: string | number }

@Injectable({
    providedIn: 'root'
})
export class TypologiesService {

    private activitiesConfigurationService = inject(ActivitiesConfigurationService);
    private communicationCenter = inject(CommunicationCenterService);
    private octopusConnect = inject(OctopusConnectService);

    private allTypologies$: Observable<Typology[]> = null;

    public typologies(): Observable<Typology[]> {
        if (!this.allTypologies$) {
            this.allTypologies$ = this.loadActivityTypologies().pipe(shareReplay(1));
        }
        return this.allTypologies$;
    }

    constructor() {
        this.communicationCenter
            .getRoom('activities')
            .getSubject('requestTypologies')
            .subscribe(() => {
                this.typologies().subscribe();
            });
    }

    public getTypologyId(typology: TypologyLabel) {
        return this.typologies().pipe(take(1)).pipe(
            map(typologies => {

                const typeSelected = typologies?.find(
                    (backendTypology) => backendTypology.label.toLowerCase() === typology.toLowerCase()
                );

                if (!typeSelected) {
                    console.warn(`Typology ${typology} not found`);
                }

                return typeSelected ? +typeSelected.id : 0;
            })
        )
    }

    /**
     * get multiple typologies ids
     * @param typologyLabel
     */
    public getTypologyIds(typologyLabel?: TypologyLabel) {
        if (typologyLabel) {
            return this.getTypologyId(typologyLabel).pipe(map(t => [t]));
        }
        const obs = this.activitiesConfigurationService.activitiesWithDefinedTypology().map((typology) => this.getTypologyId(typology));

        return combineLatest(obs).pipe(take(1))
    }

    private loadActivityTypologies() {
        return this.octopusConnect
            .loadCollection('variables/instance')
            .pipe(
                map(collection => collection.entities[0].get('activityTypes')),
                tap((typologies: Typology[]) => this.emitTypologies(typologies)),
                catchError(error => {throw new Error('Error loading typologies', error)}),
            );
    }

    private emitTypologies(typologies: Typology[]) {
        this.communicationCenter
            .getRoom('activities')
            .next('typologies', typologies);
    }
}